import React, { createContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase/supabaseClient';

// Criação do contexto
export const MarketingContext = createContext();

export const MarketingProvider = ({ children }) => {
  // Estado inicial para dados de campanhas
  const [campaigns, setCampaigns] = useState([]);
  const [channelMetrics, setChannelMetrics] = useState({
    facebook: { spent: 0, leads: 0, sales: 0, revenue: 0 },
    tiktok: { spent: 0, leads: 0, sales: 0, revenue: 0 },
    taboola: { spent: 0, leads: 0, sales: 0, revenue: 0 },
    google: { spent: 0, leads: 0, sales: 0, revenue: 0 }
  });
  const [totalInvestment, setTotalInvestment] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalROI, setTotalROI] = useState(0);
  const [totalLeads, setTotalLeads] = useState(0);
  const [totalSales, setTotalSales] = useState(0);
  const [cpa, setCPA] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Carregar dados do Supabase
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Buscar campanhas de marketing
        const { data: campaignsData, error: campaignsError } = await supabase
          .from('marketing_campaigns')
          .select('*')
          .order('date', { ascending: false });
        
        if (campaignsError) {
          console.error('Erro ao buscar campanhas de marketing:', campaignsError);
        }
        
        if (campaignsData) {
          setCampaigns(campaignsData);
        }
      } catch (error) {
        console.error('Erro ao buscar dados de marketing:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    
    // Configurar listener para mudanças
    const campaignsSubscription = supabase
      .channel('public:marketing_campaigns')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'marketing_campaigns' }, fetchData)
      .subscribe();
    
    // Limpar listener ao desmontar
    return () => {
      supabase.removeChannel(campaignsSubscription);
    };
  }, []);

  // Calcular métricas quando as campanhas mudarem
  useEffect(() => {
    // Calcular métricas por canal
    const channels = {
      facebook: { spent: 0, leads: 0, sales: 0, revenue: 0 },
      tiktok: { spent: 0, leads: 0, sales: 0, revenue: 0 },
      taboola: { spent: 0, leads: 0, sales: 0, revenue: 0 },
      google: { spent: 0, leads: 0, sales: 0, revenue: 0 }
    };

    let totalSpent = 0;
    let totalRev = 0;
    let leads = 0;
    let sales = 0;

    campaigns.forEach(campaign => {
      const channel = campaign.channel.toLowerCase();
      if (channels[channel]) {
        channels[channel].spent += parseFloat(campaign.investment || 0);
        channels[channel].leads += parseInt(campaign.leads || 0);
        channels[channel].sales += parseInt(campaign.sales || 0);
        channels[channel].revenue += parseFloat(campaign.revenue || 0);
        
        totalSpent += parseFloat(campaign.investment || 0);
        totalRev += parseFloat(campaign.revenue || 0);
        leads += parseInt(campaign.leads || 0);
        sales += parseInt(campaign.sales || 0);
      }
    });

    setChannelMetrics(channels);
    setTotalInvestment(totalSpent);
    setTotalRevenue(totalRev);
    setTotalLeads(leads);
    setTotalSales(sales);
    
    // Calcular ROI geral
    const roi = totalSpent > 0 ? ((totalRev - totalSpent) / totalSpent) * 100 : 0;
    setTotalROI(roi);
    
    // Calcular CPA (Custo por Aquisição)
    const avgCPA = sales > 0 ? totalSpent / sales : 0;
    setCPA(avgCPA);
  }, [campaigns]);

  // Adicionar nova campanha
  const addCampaign = async (newCampaign) => {
  try {
    // Conversão de data (caso esteja no formato dd/mm/yyyy)
    let formattedDate = newCampaign.date;
    if (newCampaign.date.includes('/')) {
      const [day, month, year] = newCampaign.date.split('/');
      formattedDate = `${year}-${month}-${day}`;
    }

    const payload = {
      channel: newCampaign.channel.trim().toLowerCase(),
      investment: parseFloat(newCampaign.investment || 0),
      leads: parseInt(newCampaign.leads || 0),
      sales: parseInt(newCampaign.sales || 0),
      revenue: parseFloat(newCampaign.revenue || 0),
      date: formattedDate,
      created_at: new Date().toISOString()
    };

    console.log('📦 Dados da campanha que serão enviados para Supabase:', payload);

    const { data, error } = await supabase
      .from('marketing_campaigns')
      .insert([payload])
      .select();

    if (error) {
      console.error('❌ Erro ao adicionar campanha de marketing:', error);
      return null;
    }

    console.log('✅ Campanha adicionada com sucesso:', data[0]);
    return data[0];
  } catch (error) {
    console.error('❌ Erro inesperado ao adicionar campanha de marketing:', error);
    return null;
  }
};


  // Remover campanha
  const removeCampaign = async (id) => {
    try {
      const { error } = await supabase
        .from('marketing_campaigns')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Erro ao remover campanha de marketing:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Erro ao remover campanha de marketing:', error);
      return false;
    }
  };

  // Atualizar campanha
  const updateCampaign = async (id, updatedData) => {
    try {
      const { error } = await supabase
        .from('marketing_campaigns')
        .update({
          channel: updatedData.channel,
          investment: parseFloat(updatedData.investment),
          leads: parseInt(updatedData.leads || 0),
          sales: parseInt(updatedData.sales || 0),
          revenue: parseFloat(updatedData.revenue || 0),
          date: updatedData.date,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);
      
      if (error) {
        console.error('Erro ao atualizar campanha de marketing:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Erro ao atualizar campanha de marketing:', error);
      return false;
    }
  };

  return (
    <MarketingContext.Provider
      value={{
        campaigns,
        channelMetrics,
        totalInvestment,
        totalRevenue,
        totalROI,
        totalLeads,
        totalSales,
        cpa,
        loading,
        addCampaign,
        removeCampaign,
        updateCampaign
      }}
    >
      {children}
    </MarketingContext.Provider>
  );
};
